import { Button, DatePicker, Dropdown, Radio } from 'antd'
import type { MenuProps } from 'antd'
import { Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useRef, useState } from 'react'
import {
  faClose,
  faPlay,
  faPause,
  faCirclePlay,
  faBackwardFast,
  faForwardFast,
  faDownload,
} from '@fortawesome/free-solid-svg-icons'
import type { Dayjs } from 'dayjs'
import { ChartPanel } from './ChartPanel'
import { Slider } from './Slider'
import { BaseDialog } from 'src/components/dialogs/BaseDialog'
import Styles from './DataControlPanel.module.scss'
import type { ChartPt, Station } from 'src/models'
import type { DownloadRange } from './HomePage.vm'

type DataControlPanelProps = {
  selectedStation?: Station | null
  selectedLayer?: string
  timeStepInMinutes: number
  chartData: ChartPt[]
  selectedTime?: Dayjs
  chartZoom?: { x: number; scale: number }
  timeRange?: [Dayjs, Dayjs]
  playSpeed: number
  isPlaying: boolean
  downloadSelection?: {
    stage?: 'range' | 'format'
    range?: DownloadRange | 'custom'
    dates?: [Dayjs, Dayjs]
  } | null
  onCloseChart: () => void
  onSelectDevice: (deviceId?: string) => void
  onOpenDashboard: () => void
  onSetCurrentChartX: (x?: number) => void
  onSetSelectedTime: (time: Dayjs | undefined) => void
  onTogglePlay: () => void
  onGoToLatest: () => void
  onNextPlaySpeed: () => void
  onToggleDownload: () => void
  onDownloadSelectRange: (value: DownloadRange | 'custom', dates?: [Dayjs, Dayjs]) => void
  onDownload: (format: 'csv' | 'json') => void
  onZoomChartWhenFocus: (event: WheelEvent) => void
}

const DownloadOptions: { label: string; value: DownloadRange }[] = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 24 hours', value: 'last-24h' },
  { label: 'Last 7 days', value: 'last-7d' },
  { label: 'Custom', value: 'custom' },
]

const DownloadMenuItems: MenuProps['items'] = DownloadOptions.map(option => ({
  key: option.value,
  label: option.label,
}))

const CHART_ANIMATION_MS = 220

type RenderedChart = {
  selectedStation: Station
  selectedLayer?: string
  chartData: ChartPt[]
  selectedTime?: Dayjs
  chartZoom?: { x: number; scale: number }
}

export const DataControlPanel = ({
  selectedStation,
  selectedLayer,
  timeStepInMinutes,
  chartData,
  selectedTime,
  chartZoom,
  timeRange,
  playSpeed,
  isPlaying,
  downloadSelection,
  onCloseChart,
  onSelectDevice,
  onOpenDashboard,
  onSetCurrentChartX,
  onSetSelectedTime,
  onTogglePlay,
  onGoToLatest,
  onNextPlaySpeed,
  onToggleDownload,
  onDownloadSelectRange,
  onDownload,
  onZoomChartWhenFocus,
}: DataControlPanelProps) => {
  const shouldShowChart = Boolean(selectedStation && !['cloud'].includes(selectedLayer ?? ''))
  const [renderedChart, setRenderedChart] = useState<RenderedChart | null>(null)
  const [chartExpanded, setChartExpanded] = useState(false)
  const closeTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null)
  const openFrameRef = useRef<number | null>(null)
  const openFrameRef2 = useRef<number | null>(null)
  const wasChartVisibleRef = useRef(false)

  const cancelOpenFrames = () => {
    if (openFrameRef.current) {
      window.cancelAnimationFrame(openFrameRef.current)
      openFrameRef.current = null
    }
    if (openFrameRef2.current) {
      window.cancelAnimationFrame(openFrameRef2.current)
      openFrameRef2.current = null
    }
  }

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current)
      }
      cancelOpenFrames()
    }
  }, [])

  useEffect(() => {
    if (shouldShowChart) {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }

      const isEntering = !wasChartVisibleRef.current
      wasChartVisibleRef.current = true

      if (isEntering) {
        setChartExpanded(false)
        cancelOpenFrames()
        openFrameRef.current = window.requestAnimationFrame(() => {
          openFrameRef.current = null
          openFrameRef2.current = window.requestAnimationFrame(() => {
            openFrameRef2.current = null
            setChartExpanded(true)
          })
        })
      } else {
        setChartExpanded(true)
      }
      return
    }

    wasChartVisibleRef.current = false
    cancelOpenFrames()

    if (renderedChart) {
      setChartExpanded(false)
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current)
      }
      closeTimerRef.current = window.setTimeout(() => {
        setRenderedChart(null)
        closeTimerRef.current = null
      }, CHART_ANIMATION_MS)
    }
  }, [renderedChart, shouldShowChart])

  useEffect(() => {
    if (!shouldShowChart || !selectedStation) return

    setRenderedChart({
      selectedStation,
      selectedLayer,
      chartData,
      selectedTime,
      chartZoom,
    })
  }, [chartData, chartZoom, selectedLayer, selectedStation, selectedTime, shouldShowChart])

  const isDownloadRangeOpen =
    downloadSelection?.stage === 'range' && downloadSelection?.range !== 'custom'

  return (
    <div className={Styles.dataControl} onWheel={onZoomChartWhenFocus as any}>
      {renderedChart && (
        <div
          className={`${Styles.chartShell} ${chartExpanded ? Styles.chartShellOpen : Styles.chartShellClosed}`}
        >
          <Tooltip title={'Close chart'} placement="left">
            <FontAwesomeIcon className={Styles.close} icon={faClose} onClick={onCloseChart} />
          </Tooltip>

          <ChartPanel
            selectedStation={renderedChart.selectedStation}
            selectedLayer={renderedChart.selectedLayer}
            chartData={renderedChart.chartData}
            selectedTime={renderedChart.selectedTime}
            chartZoom={renderedChart.chartZoom}
            onSelectDevice={onSelectDevice}
            onOpenDashboard={onOpenDashboard}
            onMove={onSetCurrentChartX}
          />
        </div>
      )}

      <div className={Styles.date}>{selectedTime?.format('DD MMM HH:mm')}</div>
      <Slider
        timeRange={timeRange}
        stepInMinutes={timeStepInMinutes}
        selected={selectedTime}
        onSelect={onSetSelectedTime}
      />
      <div className={Styles.controlsViewport}>
        <div className={Styles.controls}>
          <Button className={Styles.btn} type="primary" size="large" onClick={onTogglePlay}>
            <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
          </Button>
          <Button
            className={Styles.btn}
            type="primary"
            size="large"
            onClick={() => timeRange && onSetSelectedTime(timeRange[0])}
          >
            <FontAwesomeIcon icon={faBackwardFast} />
          </Button>
          <Button className={Styles.btn} type="primary" size="large" onClick={onGoToLatest}>
            <label>{'Now'}</label>
          </Button>
          <Button
            className={Styles.btn}
            type="primary"
            size="large"
            onClick={() => timeRange && onSetSelectedTime(timeRange[1])}
          >
            <FontAwesomeIcon icon={faForwardFast} />
          </Button>
          <Button className={Styles.btn} type="primary" size="large" onClick={onNextPlaySpeed}>
            <FontAwesomeIcon icon={faCirclePlay} />
            <label>{`${playSpeed}x`}</label>
          </Button>
          <Dropdown
            trigger={['click']}
            open={isDownloadRangeOpen}
            destroyOnHidden
            overlayClassName={Styles.downloadMenuOverlay}
            menu={{
              items: DownloadMenuItems,
              onClick: ({ key }) => onDownloadSelectRange(String(key) as DownloadRange),
              selectable: true,
              selectedKeys: downloadSelection?.range ? [downloadSelection.range] : [],
            }}
            onOpenChange={(open, info) => {
              if (open) {
                if (!isDownloadRangeOpen) onToggleDownload()
                return
              }

              if (info.source === 'trigger' && isDownloadRangeOpen) {
                onToggleDownload()
              }
            }}
          >
            <Button className={Styles.btn} type="primary" size="large">
              <FontAwesomeIcon icon={faDownload} />
              <label>{'Download'}</label>
            </Button>
          </Dropdown>
          {selectedStation && (
            <Button className={Styles.btn} type="primary" size="large" onClick={onOpenDashboard}>
              <label>{'Dashboard'}</label>
            </Button>
          )}
        </div>
      </div>

      <BaseDialog
        open={downloadSelection?.stage === 'format' || downloadSelection?.range === 'custom'}
        onClose={onToggleDownload}
        showDefaultActions={false}
        centered
        width={300}
      >
        {downloadSelection?.range === 'custom' && (
          <>
            <div>Select Date:</div>
            <DatePicker.RangePicker
              value={downloadSelection?.dates}
              onChange={value => {
                if (!value || !value[0] || !value[1]) return
                onDownloadSelectRange('custom', [value[0], value[1]])
              }}
            />
          </>
        )}
        {downloadSelection?.stage === 'format' && (
          <>
            <div>Select Format:</div>
            <Radio.Group
              options={[
                { label: 'CSV', value: 'csv' },
                { label: 'JSON', value: 'json' },
              ]}
              onChange={event => onDownload(event.target.value)}
              value={undefined}
              optionType="button"
              buttonStyle="solid"
            />
          </>
        )}
      </BaseDialog>
    </div>
  )
}
